import CrudApi from "@crudApi";
import { ls } from '@helpers/LocalStorageEnc';
import * as qs from 'qs';
import { formikErrorToast } from '@domNotify';
import { Flip } from 'react-toastify';
import { handleConfirmCancel } from '@components/ConfirmAlerts';
import * as mimeType from "mime-types";
import Notify from "@src/@core/components/Notify/ActionNotify";

export const isProductionEnv = () => {
  const { REACT_APP_ENV } = process.env;
  return REACT_APP_ENV === 'production';
}

export const flattenErrors = (errorsObj, path = "") => {
  let flattenedErrors = {};

  Object.keys(errorsObj).forEach((key) => {
    const errorKey = path ? `${path}.${key}` : key;

    if (typeof errorsObj[key] === "string") {
      flattenedErrors[errorKey] = errorsObj[key];
    } else if (typeof errorsObj[key] === "object" && errorsObj[key] !== null) {
      const nestedErrors = flattenErrors(errorsObj[key], errorKey);
      flattenedErrors = { ...flattenedErrors, ...nestedErrors };
    }
  });

  return flattenedErrors;
};

export const formikValidator = async ({ validateForm }, singleInstance = true, withAlert = true, maxErrorMessagesCount = 2) => {
  const currError = await validateForm();
  const errorMessages = Object.values(flattenErrors(currError));

  if (errorMessages?.length > 0 && withAlert) {
    const message = errorMessages?.length <= maxErrorMessagesCount ?
      errorMessages.reduce(
        (prevVal, value) => `${prevVal}<p class="mb-0">${value.toString()}</p>`
      )
      : 'Please Fill All Required Fields'
    formikErrorToast(
      message,
      {
        title: "Validation"
      },
      {
        autoClose: 1500,
        transition: Flip,
        hideProgressBar: false
      }
    );
  }

  if (errorMessages?.length > 0 && isProductionEnv()) {
    // if (errorMessages?.length > 0) {
    throw new Error("Must Submit Valid Data");
  }

};
export const isObject = (objValue) => {
  return objValue && typeof objValue === "object" && objValue.constructor === Object;
};

export const isFile = input => 'File' in window && input instanceof File;
export const isBlob = input => 'Blob' in window && input instanceof Blob;

// load page by path
export const loadPage = async (page, time = 250) => setTimeout(() => (window.location.href = page), time);

export const removeEmptyObjects = (o, otherKeys = ['lsExpiry'], nullOnRemove = []) => {
  otherKeys?.map(key => {
    if (key) {
      delete o[key];
    }
  })

  for (var k in o) {
    //empty object or empty array
    if ((!Array.isArray(o[k]) && isObject(o[k]) && Object.keys(o[k]).length === 0) || (Array.isArray(o[k]) && o[k].length === 0)) {
      delete o[k];
      continue;
    } else if (Array.isArray(o[k]) && o[k].length > 0) { //filter array values
      o[k] = o[k].filter((e) => e);
      continue;
    } else if ((o[k] === undefined) || (o[k] === null && nullOnRemove?.includes(o[k]))) { //remove null values and undefined values
      delete o[k];
      continue;
    } else if (!o[k] || typeof o[k] !== 'object') {
      continue; // If null or not an object, skip to the next iteration
    }
    // The property is an object
    removeEmptyObjects(o[k]);
  }
};

// important functions for react-query it mutate the object
export const removeUndefined = (object) => {
  Object.keys(object).forEach((k) => object[k] === undefined && delete object[k]);
}

export const getChangedValues = (values, initialValues) => {
  const data = Object.entries(values).reduce((acc, [key, value]) => {
    const hasChanged = initialValues[key] !== value
    if (hasChanged) {
      acc[key] = value
    }
    return acc
  }, {});

  if (Object.keys(data).length < 1) {
    Notify.Error({
      title: 'Error',
      message: 'Please Change Fields Before Submit!'
    });
    throw "Error invalid data";
  }

  return data;
}
// convert data to react-select options
export const mapData = (data) =>
  data.length
    ? data.map((e) => {
      return {
        label: e.name,
        value: e.id
      };
    })
    : [];

const isJsonResponse = (headers) => {
  const contentType = headers['content-type'] || headers['Content-Type'] || '';
  return contentType.includes('application/json') || contentType.includes('text/json');
};

const parseJsonFromBlob = async (blob) => {
  try {
    const text = await blob.text();
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const handleJsonResponse = (jsonData) => {
  const isSuccess = jsonData?.status === 'success' && Array.isArray(jsonData?.messages);

  if (isSuccess) {
    const message = jsonData.messages.join(' ') || 'Request processed successfully';
    Notify.Success({ message })
    return { success: true, message };
  }

  const errorMessage = jsonData?.message || jsonData?.error || jsonData?.messages?.[0] || 'An error occurred while downloading the file';
  Notify.Error({ message: errorMessage })
  return { success: false, message: errorMessage };
};

/**
 * Helper function to trigger file download
 */
const triggerFileDownload = (blob, fileName) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 7000);
};

/**
 * Download file from URL with automatic JSON response handling
 */
export const downloadExport = async (fileName, fileType, url, params = {}) => {
  const response = await CrudApi({
    useFormData: false,
    apiParams: { ...params, type: fileType, file_name: fileName, download: 'link' }
  }).get(url, { responseType: 'blob', accept: '*/*' });

  // Queue mode retains the small-file response and queued acknowledgment contract.
  // Stream mode returns only a small JSON link here; the browser receives the file.
  if (!isJsonResponse(response.headers)) {
    triggerFileDownload(response.data, `${fileName}.${fileType}`);
    return { success: true, downloaded: true };
  }

  const result = await parseJsonFromBlob(response.data);
  const downloadUrl = result?.data?.download_url;
  if (!downloadUrl) {
    if (result?.success === false) throw new Error(result?.messages?.[0] || 'Export failed.');
    if (!result) throw new Error('The server returned an invalid export response.');
    return result;
  }

  const link = document.createElement('a');
  link.href = downloadUrl;
  link.rel = 'noopener noreferrer';
  link.referrerPolicy = 'no-referrer';
  document.body.append(link);
  link.click();
  link.remove();
  return { success: true, downloadRequested: true };
};

export const downloadFile = async (fileName, fileType, url, params = {}) => {
  try {
    const response = await CrudApi({ useFormData: false, apiParams: { type: fileType, ...params } }).get(url, {
      responseType: 'blob',
      accept: '*/*'
    });

    // Handle JSON responses (success/error messages)
    if (isJsonResponse(response.headers)) {
      const jsonData = await parseJsonFromBlob(response.data);
      if (jsonData) {
        return jsonData;
      }
      Notify.Error({ title: "Download Failed", message: "Invalid response from server" });
      return { success: false, message: "Invalid response from server" };
    }

    // Handle file download
    const fullFileName = `${fileName}.${fileType}`;
    triggerFileDownload(response.data, fullFileName);
    return { success: true, downloaded: true };
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || 'Failed to download file';
    Notify.Error({ title: "Download Failed", message: errorMessage });
    console.error('Download error:', error);
    return { success: false, message: errorMessage };
  }
};

export const digitsOnly = (value) => /^\d+$/.test(value);

export const handleResponseErrors = (e) => {
  if (e.response) {
    return e.response;
  } else if (e.request) {
    return e.request;
  } else if (e.message) {
    return e.message;
  }
  return e;
};

export const filterBuilder = (params, useQueryString = true) => {
  let result = {};
  try {
    const { filters, ...restParams } = params;
    for (let key in filters) {
      if (key && key.indexOf(';') !== -1) {
        key.split(';').map((_k) => {
          if (_k && _k.length) {
            result[_k] = filters[key];
          }
        });
      } else {
        result[key] = filters[key];
      }
    }
    if (useQueryString) {
      return qs.stringify({
        ...restParams,
        filter: result
      });
    }
  } catch (error) { }
  return result;
};

export const mapFilters = (values) => {
  let result = [];
  try {
    let filters = [];
    if (values && values.length > 0) {
      values.map((filter) => {
        if (filter.id && filter.id.indexOf(';') > -1) {
          let d = filter.id.split(';');
          d.map((e) => filters.push({ [e]: filter.value }));
        } else {
          filters.push({ [filter.id]: filter.value });
        }
      });
    }
    result = filters.reduce((acc, f) => {
      acc[Object.keys(f)[0]] = f[Object.keys(f)[0]];
      return acc;
    }, {});
  } catch (error) { }
  return result;
};

export const getRandomKey = () => Math.random().toString(36).substring(3);

export const isBlobUrl = (url) => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === 'blob:';
  } catch (error) {
    return false;
  }
}

export const sleep = (milliseconds) => new Promise(resolve => setTimeout(resolve, milliseconds));

// titleCase / humanText / getDefaultOption moved to @src/utility/Utils so the
// pdf worker can use them without dragging this module's browser-only imports
export { titleCase, humanText, getDefaultOption } from "@src/utility/Utils";
export const removePunctuation = (text) => {
  return text?.length && text?.replace(/[^a-zA-Z0-9ء-ي]/g, "") || '';
}

export const prepareValues = (values, keys = []) => {

  let customValues = { ...values }

  keys?.forEach(key => {
    customValues = {
      ...customValues,
      [key]: [
        values[key]
      ]
    }
  });
  return customValues
};

export const deleteTableRecord = (deleteRecord, id, title = 'Delete', text = '"This item will be deleted, kindly confim?"') => {
  handleConfirmCancel({
    title,
    text,
    type: 'error',
    buttons: [
      {
        text: 'Delete', color: 'danger', onClick: () => {
          deleteRecord(id);
        }
      },
      { text: 'Cancel' }
    ]
  });
};

export function hasDuplicates(arr = [], fields = []) {

  const uniqueItems = [];

  for (let item of arr) {
    const identifier = fields?.map(field => item[field]).join('-');

    if (uniqueItems.includes(identifier) && uniqueItems?.length) {
      return true;
    }

    uniqueItems.push(identifier);
  }
}

export const getBase64 = async (filePath) => {
  const response = await fetch(filePath);
  const blob = await response.blob();
  const reader = new FileReader();

  return new Promise((resolve, reject) => {
    reader.onloadend = () => {
      const mimeType = blob.type || "application/octet-stream"; // استخدام MIME Type تلقائيًا
      resolve(`data:${mimeType};base64,${reader.result.split(',')[1]}`);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const generateGradient = (color, percent = 30) => {
  if (!color) return {}
  const num = parseInt(color.slice(1), 16)
  const r = (num >> 16) + Math.round(2 * percent);
  const g = ((num >> 8) & 0x00ff) + Math.round(2 * percent);
  const b = (num & 0x0000ff) + Math.round(2 * percent);

  const newColor = (
    0x1000000 +
    (r < 255 ? (r < 0 ? 0 : r) : 255) * 0x10000 +
    (g < 255 ? (g < 0 ? 0 : g) : 255) * 0x100 +
    (b < 255 ? (b < 0 ? 0 : b) : 255)
  )
    .toString(16)
    .slice(1);

  return {
    background: `linear-gradient(135deg, ${color}, #${newColor})`
  };
}

export const extractFileExtension = file => {
  if (!file instanceof File) return;
  if (typeof file === 'string') return `.${file?.split('.')?.pop()}`;
  const filename = file?.name;
  const mimetype = mimeType?.lookup(filename);
  const type = mimeType.extension(mimetype);
  return type ? `.${type}` : `.${filename?.split('.')?.pop()}`
}

export const handleViewAsByAccessibleBranch = ({
  hasManager = true,
  managerLabel = 'HSE Manager',
  managerValue = 'qhse_manager',
  hasEngineer = true,
  engineerLabel = 'HSE Engineer',
  engineerValue = 'qhse_engineer',
  hasRep = false,
  repLabel = 'HSE Rep',
  repValue = 'qhse_rep',
} = {}) => { // destructuring the object to make it optional
  const selectedBranchesOptions = ls.getItem('selectedBranchesOptions') || [];
  const list = selectedBranchesOptions?.map(item => [
    hasManager && {
      label: `${item?.name} ${managerLabel}`,
      key: `is_${item?.id}_${managerValue}`,
      value: `${item?.id}_${managerValue}`,
    },
    hasEngineer && {
      label: `${item?.name} ${engineerLabel}`,
      key: `is_${item?.id}_${engineerValue}`,
      value: `${item?.id}_${engineerValue}`,
    },
    hasRep && {
      label: `${item?.name} ${repLabel}`,
      key: `is_${item?.id}_${repValue}`,
      value: `${item?.id}_${repValue}`,
    },
  ])
  return list?.flat()
}

export const handleListButtonClick = () => {
  const listButton = document.getElementById('pixel-index-list-button');
  listButton?.click();
}

export const formatNumberWithSeparators = (number, decimalPlaces = 0) => {
  if (number === null || number === undefined || isNaN(number)) return '0';

  // Round to specified decimal places
  const roundedNumber = decimalPlaces > 0 ?
    Number(number).toFixed(decimalPlaces) :
    Math.round(Number(number));

  // Split by decimal point if exists
  const parts = roundedNumber.toString().split('.');

  // Add commas to integer part
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Return formatted number
  return parts.join('.');
}

// Helper function to convert hex color to RGB
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Helper function to convert RGB to hex color
export const rgbToHex = (r, g, b) => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

// Helper function to lighten a hex color by a percentage
export const lightenColor = (hex, percent = 90) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.round(rgb.r + (255 - rgb.r) * (percent / 100));
  const g = Math.round(rgb.g + (255 - rgb.g) * (percent / 100));
  const b = Math.round(rgb.b + (255 - rgb.b) * (percent / 100));

  return rgbToHex(r, g, b);
};
